import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { PaymentRecordedDetail, usePaymentItem } from '#/entities/shipment'
import type { ShipmentPaymentItem } from '#/entities/shipment'
import { useGetShipmentApiV1ShipmentsShipmentIdGet } from '#/shared/api/generated/shipment/theCargoShipment'
import { Button } from '#/shared/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
} from '#/shared/ui/dialog'
import { ChevronLeftIcon, CloseIcon } from '#/shared/ui/icons'

import { SETTLE_METHOD_LABEL, SETTLE_METHOD_ORDER } from '../model/methods'
import {
  SETTLE_VIA,
  SETTLE_VIEW,
  useSettlePayment,
} from '../model/use-settle-payment'

import { AddCardPanel } from './add-card-panel'
import { CardForm } from './card-form'
import { OtherMethodForm } from './other-method-form'
import { PaymentSummary } from './payment-summary'
import { PaymentViaTabs } from './payment-via-tabs'

interface SettlePaymentModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  item: ShipmentPaymentItem
  shipmentId: string | undefined
}

interface SummaryFromItemProps {
  item: ShipmentPaymentItem
}

function SummaryFromItem({ item }: SummaryFromItemProps) {
  const { t } = useTranslation()
  const typeLabel = t(`settlePayment.type.${item.paymentType}`)
  const flowLabel = t(`settlePayment.flow.${item.flow.direction ?? 'unknown'}`)
  const metaParts: ReadonlyArray<string> = [
    item.timestamp
      ? t('settlePayment.summary.createdOn', { date: item.timestamp })
      : '',
    ...SETTLE_METHOD_ORDER.slice(0, 3).map((m) => SETTLE_METHOD_LABEL[m]),
  ]
  return (
    <PaymentSummary
      typeLabel={typeLabel}
      flowLabel={flowLabel}
      flowVariant={item.flow.variant}
      amount={item.amountFormatted}
      metaParts={metaParts}
    />
  )
}

function ModalHeader({
  title,
  onBack,
}: {
  title: string
  onBack?: () => void
}) {
  const { t } = useTranslation()
  return (
    <header className="flex h-13 shrink-0 items-center justify-between gap-2.5 border-b border-gray-200 px-5 py-2.5">
      <div className="flex min-w-0 items-center gap-1.5">
        {onBack && (
          <button
            type="button"
            aria-label={t('settlePayment.back')}
            onClick={onBack}
            className="-ml-1.5 flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-md text-gray-500 hover:bg-gray-50 focus-visible:outline-none focus-visible:focus-ring"
          >
            <ChevronLeftIcon className="size-4" />
          </button>
        )}
        <DialogTitle className="truncate text-lg font-semibold text-gray-900">
          {title}
        </DialogTitle>
      </div>
      <DialogClose
        aria-label={t('settlePayment.close')}
        className="flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-md text-gray-500 hover:bg-gray-50 focus-visible:outline-none focus-visible:focus-ring"
      >
        <CloseIcon className="size-3.5" />
      </DialogClose>
    </header>
  )
}

function ModalFooter({
  primaryLabel,
  onPrimary,
  primaryDisabled,
  primaryLoading,
}: {
  primaryLabel: string
  onPrimary: () => void
  primaryDisabled: boolean
  primaryLoading: boolean
}) {
  const { t } = useTranslation()
  return (
    <footer className="flex shrink-0 items-center justify-end gap-2 border-t border-gray-200 px-5 py-3">
      <DialogClose asChild>
        <Button
          type="button"
          variant="outline"
          size="xs"
          pressEffect
          className="px-3.75"
        >
          {t('settlePayment.cancel')}
        </Button>
      </DialogClose>
      <Button
        type="button"
        size="xs"
        pressEffect
        className="px-3.75"
        disabled={primaryDisabled || primaryLoading}
        onClick={onPrimary}
      >
        {primaryLoading ? t('settlePayment.submitting') : primaryLabel}
      </Button>
    </footer>
  )
}

function DoneFooter({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation()
  return (
    <footer className="flex shrink-0 items-center justify-end gap-2 border-t border-gray-200 px-5 py-3">
      <Button
        type="button"
        size="xs"
        pressEffect
        className="px-3.75"
        onClick={onDone}
      >
        {t('settlePayment.done')}
      </Button>
    </footer>
  )
}

type SettleState = ReturnType<typeof useSettlePayment>

function FormBody({
  item,
  settle,
  onAddCard,
}: {
  item: ShipmentPaymentItem
  settle: SettleState
  onAddCard: () => void
}) {
  return (
    <>
      <SummaryFromItem item={item} />
      <PaymentViaTabs value={settle.via} onChange={settle.setVia} />
      {settle.via === SETTLE_VIA.card ? (
        <CardForm
          savedCards={settle.savedCards}
          selectedCardId={settle.selectedCardId}
          onSelectCard={settle.setSelectedCardId}
          isLoading={settle.isCardsLoading}
          amount={settle.amount}
          onAmountChange={settle.setAmount}
          amountOverLimit={settle.isAmountOverLimit}
          chargeTotalLabel={item.breakdown.total}
          reference={settle.reference}
          onReferenceChange={settle.setReference}
          onAddCard={onAddCard}
        />
      ) : (
        <OtherMethodForm
          selectedMethod={settle.selectedMethod}
          onSelectMethod={settle.onSelectMethod}
          recordedMethods={settle.recordedMethods}
          methods={settle.availableMethods}
          isMethodsLoading={settle.isMethodsLoading}
          proofFiles={settle.proofFiles}
          onAddProofFiles={settle.addProofFiles}
          onRemoveProofFile={settle.removeProofFile}
          amount={settle.amount}
          onAmountChange={settle.setAmount}
          amountOverLimit={settle.isAmountOverLimit}
          chargeTotalLabel={item.breakdown.total}
          date={settle.date}
          onDateChange={settle.setDate}
          reference={settle.reference}
          onReferenceChange={settle.setReference}
        />
      )}
    </>
  )
}

function useShipmentCustomer(
  open: boolean,
  shipmentId: string | undefined,
): { email: string | undefined; id: string | undefined } {
  const shipmentQuery = useGetShipmentApiV1ShipmentsShipmentIdGet(
    shipmentId ?? '',
    { query: { enabled: open && Boolean(shipmentId) } },
  )
  return {
    email: shipmentQuery.data?.data.customer?.email ?? undefined,
    id: shipmentQuery.data?.data.customer?.id ?? undefined,
  }
}

function FormPanel({
  item,
  settle,
  onSubmit,
  onAddCard,
}: {
  item: ShipmentPaymentItem
  settle: SettleState
  onSubmit: () => void
  onAddCard: () => void
}) {
  const { t } = useTranslation()
  const isCard = settle.via === SETTLE_VIA.card
  const chargeLabel = settle.isPartialCharge
    ? t('settlePayment.cta.chargePartial', {
        amount: settle.chargeAmountLabel,
        total: item.breakdown.total,
      })
    : t('settlePayment.cta.charge', { amount: settle.chargeAmountLabel })
  const submitLabel = isCard ? chargeLabel : t('settlePayment.cta.markAsPaid')
  const submitDisabled = isCard
    ? !settle.selectedCardId || settle.isAmountOverLimit
    : !settle.selectedMethod || settle.isAmountOverLimit
  return (
    <>
      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-5 pt-4 pb-4">
        <FormBody item={item} settle={settle} onAddCard={onAddCard} />
      </div>
      <ModalFooter
        primaryLabel={submitLabel}
        onPrimary={onSubmit}
        primaryDisabled={submitDisabled}
        primaryLoading={settle.isSubmitting}
      />
    </>
  )
}

function RecordedPanel({
  paymentId,
  enabled,
  onClose,
}: {
  paymentId: string
  enabled: boolean
  onClose: () => void
}) {
  return (
    <>
      <PaymentRecordedDetail paymentId={paymentId} enabled={enabled} />
      <DoneFooter onDone={onClose} />
    </>
  )
}

function resolveTitle(
  t: ReturnType<typeof useTranslation>['t'],
  addCardOpen: boolean,
  isFormView: boolean,
): string {
  if (addCardOpen) return t('addCard.title')
  return isFormView
    ? t('settlePayment.title')
    : t('settlePayment.recordedTitle')
}

export function SettlePaymentModal({
  open,
  onOpenChange,
  item,
  shipmentId,
}: SettlePaymentModalProps) {
  const { t } = useTranslation()
  const { item: payment, methods } = usePaymentItem(item.id, item, open)
  const customer = useShipmentCustomer(open, shipmentId)
  const settle = useSettlePayment({
    open,
    paymentId: payment.id,
    shipmentId,
    customerId: customer.id,
    defaultAmount: payment.breakdown.total.replace(/[^\d.-]/g, ''),
    methods,
  })
  const [addCardOpen, setAddCardOpen] = useState(false)
  const isFormView = settle.view === SETTLE_VIEW.form

  useEffect(() => {
    if (!open) setAddCardOpen(false)
  }, [open])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[560px]">
        <ModalHeader
          title={resolveTitle(t, addCardOpen, isFormView)}
          onBack={addCardOpen ? () => setAddCardOpen(false) : undefined}
        />
        {addCardOpen ? (
          <AddCardPanel
            customerId={customer.id}
            customerEmail={customer.email}
            onBack={() => setAddCardOpen(false)}
          />
        ) : isFormView ? (
          <FormPanel
            item={payment}
            settle={settle}
            onSubmit={settle.submit}
            onAddCard={() => setAddCardOpen(true)}
          />
        ) : (
          <RecordedPanel
            paymentId={payment.id}
            enabled={open}
            onClose={() => onOpenChange(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}
